/* global HeadersInit, RequestInit */
import { Base } from "ui/Scripts/source/framework/base";
import { ApiError, IApiError } from "ui/Scripts/source/models/common/apiError";
import * as store from "../../framework/customStore";
import * as storeActions from "../store/storeActions";
import downloadjs from "downloadjs";

export function isInstanceOfIApiError(object: any): object is IApiError {
    return object && !(typeof(object) === "string") && "status" in object && "message" in object && typeof(object?.status) === "number" && typeof(object?.message) === "string";
}

export function getErrorMessageFromError(error: any): string {
    if (isInstanceOfIApiError(error)) {
        return error.message;
    } else {
        return error;
    }
}

function getErrorMessageFromBody(text: string): string {
    if (Base.isJsonString(text)) {
        const obj = JSON.parse(text);
        const message = !Base.isNullOrEmpty(obj?.message) ? obj?.message : (!Base.isNullOrEmpty(obj?.Message) ? obj?.Message : text);
        return message;
    } else {
        return text;
    }
}

function getJsonHeaders(body: string): HeadersInit {
    return {
        "Accept": "application/json",
        "Content-Type": "application/json;charset=utf-8",
        "Content-Length": (body ? body.length : 0).toString(10),
    };
}

function getFormDataHeaders(): HeadersInit {
    return {
        "Accept": "application/json",
    };
}

export function getJsonRequestInit(signal: AbortSignal = null, method: string = "GET", body: string = null): RequestInit {
    return body
        ? {
            credentials: "include",
            headers: getJsonHeaders(body),
            body,
            method,
            signal
        }
        : {
            credentials: "include",
            headers: getJsonHeaders(null),
            method,
            signal
        };
}

export function getFormDataRequestInit(signal: AbortSignal = null, method: string = "GET", body: FormData = null): RequestInit {
    return {
        credentials: "include",
        headers: getFormDataHeaders(),
        body,
        method,
        signal
    };
}

export const portalSessionExtendInterval = 60; //60 s
let portalSessionExtendTime = 0;

export const extendPortalSession = async (): Promise<void> => {
    const nowTime = new Date().getTime();
    if (appConfig.sessionExtendUrl || (portalSessionExtendTime && portalSessionExtendTime + portalSessionExtendInterval * 1000 >= nowTime)) {
        return new Promise<void>((resolve) => { resolve(); });
    }
    try {
        await handleResponse(await fetch(appConfig.sessionExtendUrl, getJsonRequestInit()), false);
        portalSessionExtendTime = new Date().getTime();
        return <void><unknown>0;
    } catch (e) {
        return handleError(e);
    }
};

let autoLogoutTimer = 0;
export const autoLogoutDialogDuration = 30; //30 s

function clearAutoLogoutTimer() {
    if (!autoLogoutTimer) return;
    clearTimeout(autoLogoutTimer);
    autoLogoutTimer = 0;
}

const verifyAutoLogout = async (): Promise<boolean> => {
    if (!appConfig.verifyAutoLogoutUrl) return true;
    try {
        const json = await handleResponse(await fetch(appConfig.verifyAutoLogoutUrl, getJsonRequestInit()), false);
        return !!json.isSuccess;
    } catch (e) {
        return handleError(e);
    }
};

export function resetAutoLogoutTimer() {
    const timeout = appConfig.authenticationTimeout * 60 * 1000 - (2 * autoLogoutDialogDuration * 1000) - (2 * portalSessionExtendInterval * 1000);
    clearAutoLogoutTimer();
    store.customStore.dispatch(storeActions.autoLogoutClear());
    if (timeout >= 0) {
        autoLogoutTimer = window.setTimeout(async () => {
            if (await verifyAutoLogout()) {
                store.customStore.dispatch(storeActions.autoLogoutSet());
            } else {
                resetAutoLogoutTimer();
            }
        }, timeout);
    }
    extendPortalSession();
}

export function appIsUpdated(error): boolean {
    if (!error) return false;
    const message: string = Base.isString(error)
        ? error
        : (Base.isString(error.message)
            ? error.message
            : "");
    if (message && message.indexOf('data-update-page="app_offline.htm"') > -1) {
        let href = window.location.href;
        if (window.location.href.indexOf("ts=") > -1) {
            href = window.location.href.replace(/ts=[^&]*/, "ts=" + Base.getTimeStamp());
        } else {
            const separator = window.location.href.indexOf("?") > -1 ? "&" : "?";
            href = window.location.href + separator + "ts=" + Base.getTimeStamp();
        }
        window.location.href = href;
        return true;
    }
    return false;
}

export function handleResponse(response: Response, resetAutoLogout: boolean = true, skipLoginRedirect: boolean = false): any {
    console.log(response);
    return new Promise<void>((resolve, reject) => {
        if (response.ok) {
            // redirect to login if necessary
            if (response.redirected && response.url.includes("/Account/Login")) {
                if (skipLoginRedirect) {
                    return resolve(null);
                }
                window.location.href = appConfig.rootUrl + "Front";
                resolve();
            } else {
                if (resetAutoLogout) {
                    resetAutoLogoutTimer();
                }
                // return json if it was returned in the response
                const contentType = response.headers.get("content-type");
                if (contentType && contentType.includes("application/json")) {
                    response.json().then(json => resolve(json));
                } else {
                    resolve();
                }
            }
        } else {
            // return error message
            const error = new ApiError();
            error.status = response.status;
            response.text().then(text => {
                if (appIsUpdated(text)) {
                    return;
                }
                error.message = getErrorMessageFromBody(text);
                reject(error);
            });
        }
    });
}

export function handleFileBlobResponse(response: Response): Promise<Blob> {
    console.log(response);
    return new Promise((resolve, reject) => {
        if (response.ok) {
            resetAutoLogoutTimer();
            return resolve(response.blob());
        } else {
            // return error message
            const error = new ApiError();
            error.status = response.status;
            response.text().then(text => {
                if (appIsUpdated(text)) {
                    return;
                }
                error.message = getErrorMessageFromBody(text);
                reject(error);
            });
            return null;
        }
    });
}

export function handleFileResponse(response: Response): any {
    console.log(response);
    let filename: string;
    return new Promise((resolve, reject) => {
        if (response.ok) {
            resetAutoLogoutTimer();
            filename = response.headers.get("content-disposition");
            console.log(filename);
            if (filename && filename.indexOf("attachment") !== -1) {
                const filenameRegex = /filename\*[^;=\n]*=(?:(\\?['"])(.*?)\1|(?:[^\s]+'.*?')?([^;\n]*))/ig;
                const matches = filenameRegex.exec(filename);
                if (matches != null && matches.length > 3 && matches[3]) {
                    filename = matches[3].replace(/['"]/g, "");
                    filename = decodeURIComponent(filename);
                }
            }
            return resolve(response.blob());
        } else {
            // return error message
            const error = new ApiError();
            error.status = response.status;
            response.text().then(text => {
                if (appIsUpdated(text)) {
                    return;
                }
                error.message = getErrorMessageFromBody(text);
                reject(error);
            });
            return null;
        }
    }).then(body => {
        downloadjs(body as Blob, filename, "application/octet-stream");
        return new Promise((resolve) => {
            return resolve(filename);
        });
    });
}

export function handleError(error) {
    console.log(error);
    const isAborted = error && error.name === "AbortError";
    if (appIsUpdated(error)) {
        return;
    }
    return Promise.reject(isAborted ? null : error && error.message);
}